
import {
  defineComponent,
  Ref,
  ref,
  reactive,
  computed,
  onMounted,
  nextTick,
} from 'vue'
import { Post, SettingsStatus } from '@/model/data-object/Post'
import AButton from '@/components/Forms/AButton.vue'
import AIcon from '@/components/AIcon.vue'
import PostCreate from '@/components/Post/PostCreate.vue'
import usePostVote from '@/composables/post-vote/postVote'
import { DateTimeHelper } from '@/helper/dateTime'
import useCurrentUser from '@/composables/user/user'
import useAppState from '@/composables/ui/ui'
import { useRouter } from '@/routing/router'
import { useSubscribePost } from '@/composables/subscribers/subscribersManager'
import LoginBanner from '@/components/Post/LoginBanner.vue'
import PostSingleViolation from '@/components/Post/PostSingleViolation.vue'
import useScrollManager from '@/composables/scroller/useScrollManager'
import PostCopyUrl from '@/components/Post/Tooltips/PostCopyUrl.vue'
import PostVoteTooltip from '@/components/Post/Tooltips/PostVoteTooltip.vue'
import PostSuperLikeTooltip from '@/components/Post/Tooltips/PostSuperLikeTooltip.vue'
import PostSubscribeTooltip from '@/components/Post/Tooltips/PostSubscribeTooltip.vue'
import PostSuperLikeLoginTooltip from '@/components/Post/Tooltips/PostSuperLikeLoginTooltip.vue'
import PostSuperLikeQuotaTooltip from '@/components/Post/Tooltips/PostSuperLikeQuotaTooltip.vue'
import PostSuperLikeUpgradeTooltip from '@/components/Post/Tooltips/PostSuperLikeUpgradeTooltip.vue'
import PostReportConfirmationTooltip from '@/components/Post/Tooltips/PostReportConfirmationTooltip.vue'
import PostEditTooltip from '@/components/Post/Tooltips/PostEditTooltip.vue'
import PostSuperLikeBuySubscriptionTooltip from '@/components/Post/Tooltips/PostSuperLikeBuySubscriptionTooltip.vue'
import PostBannedTooltip from '@/components/Post/Tooltips/PostBannedTooltip.vue'
import PostVoteLoginTooltip from '@/components/Post/Tooltips/PostVoteLoginTooltip.vue'
import PostSubscribeLoginTooltip from '@/components/Post/Tooltips/PostSubscribeLoginTooltip.vue'
import {
  TOOLTIP_COMPONENTS,
  usePostTooltip,
} from '@/composables/post/postTooltip'
import useAnalyticsManager, {
  ANALYTICS_EVENT_POST_COPY_LINK,
  ANALYTICS_EVENT_POST_REPORT,
  ANALYTICS_EVENT_POST_RESPOND,
} from '@/composables/analytics/analyticsManager'
import useConfiguration from '@/composables/configuration/configuration'
import useUiFactory from '@/composables/ui/uiFactory'
import PostRespondBanned from '@/components/Post/PostRespondBanned.vue'
import usePostSingleUiActions from '@/composables/ui/uiActions'
import AError from '@/components/Post/SinglePost/AError.vue'
import PostSingleParent from '@/components/Profile/PostSingleParent.vue'
import PostAuthor from '@/components/Post/SinglePost/PostAuthor.vue'
import PostInformation from '@/components/Post/SinglePost/PostInformation.vue'
import usePostCreate from '@/composables/post/postCreate'
import PostEdit from '@/components/Post/SinglePost/PostEdit.vue'
import ALoader from '@/components/ALoader.vue'
import { usePostSuperLikeTooltip } from '@/composables/post-super-like/postSuperLike'
import { usePostEditTooltip } from '@/composables/post/postEdit'
import PostContent from '@/components/Post/SinglePost/PostContent.vue'

export default defineComponent({
  name: 'PostSingle',
  emits: ['post-responded'],
  components: {
    PostContent,
    ALoader,
    PostEdit,
    PostInformation,
    PostSuperLikeUpgradeTooltip,
    PostEditTooltip,
    PostSuperLikeLoginTooltip,
    PostSuperLikeQuotaTooltip,
    PostSubscribeLoginTooltip,
    PostSuperLikeBuySubscriptionTooltip,
    PostAuthor,
    PostSingleParent,
    AError,
    PostRespondBanned,
    PostVoteTooltip,
    PostSuperLikeTooltip,
    PostCopyUrl,
    PostSingleViolation,
    LoginBanner,
    AButton,
    AIcon,
    PostSubscribeTooltip,
    PostReportConfirmationTooltip,
    PostCreate,
    PostBannedTooltip,
    PostVoteLoginTooltip,
  },
  data() {
    return {
      footerIsActive: false,
      editing: false,
    }
  },
  props: {
    postRoot: {
      type: Object, // todo change type to post
      default: {},
    },
    nested: {
      type: Boolean,
      default: false,
    },
    boxPost: {
      type: Boolean,
      default: false,
    },
    showReportButton: {
      type: Boolean,
      default: false,
    },
    hideShareButton: {
      type: Boolean,
      default: false,
    },
    hideRespondButton: {
      type: Boolean,
      default: false,
    },
    hideVoteButton: {
      type: Boolean,
      default: false,
    },
    stripLongText: {
      type: Boolean,
      default: false,
    },
    stripLongTextLength: {
      type: Number,
      default: 235,
    },
    stripLongTextSuffix: {
      type: String,
      default: ' ',
    },
    stripLongTextButton: {
      type: Boolean,
      default: true,
    },
    showSubscribeButton: {
      type: Boolean,
      default: false,
    },
    showRedirectToThreadButton: {
      type: Boolean,
      default: false,
    },
    showArticleLink: {
      type: Boolean,
      default: false,
    },
    allowShowNew: {
      type: Boolean,
      default: false,
    },
    highlightMine: {
      type: Boolean,
      default: false,
    },
    showIp: {
      type: Boolean,
      default: false,
    },
    hideUsername: {
      type: Boolean,
      default: false,
    },
    hideUserBlog: {
      type: Boolean,
      default: false,
    },
    openRespondPage: {
      type: Boolean,
      default: false,
    },
    showLinkToTopic: {
      type: Boolean,
      default: false,
    },
    allowAuthorSeenBadge: {
      type: Boolean,
      default: false,
    },
    showAuthorAbove: {
      type: Boolean,
      default: false,
    },
    showContentAuthor: {
      type: String,
      default: '',
    },
    hideFooter: {
      type: Boolean,
      default: false,
    },
    showParentAnnotation: {
      type: Boolean,
      default: false,
    },
    showParentAuthorRef: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    dateTimeFormatted() {
      return DateTimeHelper.formatDateTime(this.postRoot.post.createdAt)
    },
    isHidden() {
      return [SettingsStatus.HIDDEN, SettingsStatus.GDPR_DELETED].includes(
        this.postRoot.post.status
      )
    },
    expandTruncate(text: any, target: any) {
      if (target === undefined) {
        return false
      }

      target.innerHTML = text
      target.classList.add('is-expanded')
    },
    truncate(text: any, length: number, suffix: any) {
      if (text.length > length) {
        return text.substring(0, length) + suffix
      } else {
        return text
      }
    },
  },
  setup(props, context) {
    const { currentUser, allowPayingCustomerAction } = useCurrentUser()
    const {
      postIdToShow,
      loggedIn,
      lastSavedPost,
      firstForumVisit,
    } = useAppState()
    const newPost = reactive<Post | object>({})
    const isResponding: Ref<boolean> = ref(false)
    const showLoginText: Ref<boolean> = ref(false)
    const showOnlySubsText: Ref<boolean> = ref(false)
    const { respondToPostId } = useAppState()
    const { postRespond } = usePostCreate()
    const { alreadyPerformed, currentComponent, changed } = usePostTooltip()

    const isSelectedPost = computed(() => {
      return postIdToShow.value === props.postRoot.post.id
    })

    const isLastSavedPost = computed(() => {
      return (
        lastSavedPost.value && lastSavedPost.value.id === props.postRoot.post.id
      )
    })

    function createdPost(post: Post) {
      isResponding.value = false
      context.emit('post-responded', post)
      postRespond(post)
    }

    const propagatePostRespondEvent = () => {
      const { propagateAnalyticsEvent } = useAnalyticsManager()
      propagateAnalyticsEvent(
        props.postRoot.post as Post,
        ANALYTICS_EVENT_POST_RESPOND
      )
    }

    const redirectToShowRespondForm = () => {
      propagatePostRespondEvent()
      const { reloadHash } = useUiFactory()
      reloadHash(['respondToPostId'])
    }

    const showResponseForm = () => {
      if (!isResponding.value) {
        propagatePostRespondEvent()
      }
      if (allowPayingCustomerAction.value) {
        if (currentUser.value?.isBanned) {
          currentComponent.value = TOOLTIP_COMPONENTS.POST_BANNED

          return
        }

        isResponding.value = !isResponding.value

        return
      }
      if (loggedIn.value) {
        showOnlySubsText.value = !showOnlySubsText.value

        return
      }

      showLoginText.value = !showLoginText.value
    }

    if (respondToPostId.value === props.postRoot.post.id) {
      showResponseForm()
    }

    onMounted(() => {
      nextTick(() => {
        props.postRoot.mounted = true

        if (isLastSavedPost.value && !isSelectedPost.value) {
          const { scrollToLastSaved } = useScrollManager()
          scrollToLastSaved()
        }
      })
    })

    const copyLink = () => {
      if (currentComponent.value !== TOOLTIP_COMPONENTS.POST_COPY) {
        const { propagateAnalyticsEvent } = useAnalyticsManager()
        propagateAnalyticsEvent(
          props.postRoot.post as Post,
          ANALYTICS_EVENT_POST_COPY_LINK
        )
        currentComponent.value = TOOLTIP_COMPONENTS.POST_COPY

        return
      }

      currentComponent.value = undefined
    }

    const { configuration } = useConfiguration()

    const postLinkDisabled = () => {
      return configuration.widget.postDateLinkDisabled
    }
    const postReportQuestion = () => {
      if (
        currentComponent.value === TOOLTIP_COMPONENTS.POST_REPORT_CONFIRMATION
      ) {
        currentComponent.value = undefined

        return
      }
      const { propagateAnalyticsEvent } = useAnalyticsManager()
      propagateAnalyticsEvent(
        props.postRoot.post as Post,
        ANALYTICS_EVENT_POST_REPORT
      )
      currentComponent.value = TOOLTIP_COMPONENTS.POST_REPORT_CONFIRMATION
    }

    const postUiActions = usePostSingleUiActions(props.postRoot.post as Post)

    return {
      configuration,
      newPost,
      postIdToShow,
      isResponding,
      firstForumVisit,
      isSelectedPost,
      showLoginText,
      showOnlySubsText,
      loggedIn,
      currentComponent,
      alreadyPerformed,
      changed,
      isLastSavedPost,
      postLinkDisabled,
      copyLink,
      createdPost,
      propagatePostRespondEvent,
      redirectToShowRespondForm,
      showResponseForm,
      postReportQuestion,
      ...useSubscribePost(
        props.postRoot.post as Post,
        currentComponent,
        alreadyPerformed
      ),
      ...usePostSuperLikeTooltip(props.postRoot.post as Post, currentComponent),
      ...useRouter(),
      ...postUiActions,
      ...usePostVote(currentComponent, alreadyPerformed, changed),
      ...usePostEditTooltip(currentComponent, postUiActions.postEditing),
    }
  },
})
